export type TradePanelUnavailableStage = 'estimate' | 'pool_snapshot' | 'wallet_balance';

export type TradePanelValidationReason =
  | 'insufficient_balance'
  | 'insufficient_for_fees'
  | 'market_backing'
  | 'market_created'
  | 'market_graduated'
  | 'market_resolved'
  | 'no_holdings'
  | 'swap_no_holdings'
  | 'trading_restricted';

export type TradePanelState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'loading'; readonly stage: 'pool_snapshot' | 'wallet_balance' }
  | { readonly kind: 'refreshing'; readonly stage: 'pool_snapshot' | 'wallet_balance' }
  | { readonly kind: 'ready' }
  | { readonly kind: 'validation_blocked'; readonly reason: TradePanelValidationReason }
  | {
      readonly kind: 'unavailable';
      readonly stage: TradePanelUnavailableStage;
      readonly reason: string;
    };

interface TradePanelStateInput {
  readonly hasPositiveAmount: boolean;
  readonly lifecycleReason?: TradePanelValidationReason;
  readonly pool:
    | { readonly status: 'loading' }
    | { readonly status: 'refreshing' }
    | { readonly status: 'ready' }
    | { readonly status: 'unavailable'; readonly reason: string };
  readonly walletBalance:
    | { readonly status: 'loading' }
    | { readonly status: 'not_required' }
    | { readonly status: 'refreshing' }
    | { readonly status: 'ready' }
    | { readonly status: 'unavailable'; readonly reason: string };
  readonly estimate:
    | { readonly status: 'not_requested' }
    | { readonly status: 'ready' }
    | { readonly status: 'unavailable'; readonly reason: string };
  readonly validationReason?: TradePanelValidationReason;
  /** Jailed account on a side that acquires outcome tokens (buy or swap). */
  readonly buyRestricted?: boolean;
}

export function deriveTradePanelState({
  hasPositiveAmount,
  lifecycleReason,
  pool,
  walletBalance,
  estimate,
  validationReason,
  buyRestricted = false,
}: TradePanelStateInput): TradePanelState {
  if (lifecycleReason !== undefined) {
    return { kind: 'validation_blocked', reason: lifecycleReason };
  }
  if (buyRestricted) return { kind: 'validation_blocked', reason: 'trading_restricted' };
  if (!hasPositiveAmount) return { kind: 'idle' };

  if (pool.status === 'loading') return { kind: 'loading', stage: 'pool_snapshot' };
  if (pool.status === 'unavailable') {
    return { kind: 'unavailable', stage: 'pool_snapshot', reason: pool.reason };
  }
  if (walletBalance.status === 'loading') {
    return { kind: 'loading', stage: 'wallet_balance' };
  }
  if (walletBalance.status === 'unavailable') {
    return { kind: 'unavailable', stage: 'wallet_balance', reason: walletBalance.reason };
  }

  if (pool.status === 'refreshing') {
    return { kind: 'refreshing', stage: 'pool_snapshot' };
  }
  if (walletBalance.status === 'refreshing') {
    return { kind: 'refreshing', stage: 'wallet_balance' };
  }
  if (validationReason !== undefined) {
    return { kind: 'validation_blocked', reason: validationReason };
  }
  if (estimate.status === 'unavailable') {
    return { kind: 'unavailable', stage: 'estimate', reason: estimate.reason };
  }
  if (estimate.status === 'not_requested') return { kind: 'idle' };
  return { kind: 'ready' };
}
